import type { ApiConversationChannel } from '../../api';
import { AnalyticsMetric } from '../analytics/const';

export type ConversationChannel = ApiConversationChannel | 'ALL';
export const ALL_CHANNEL: ConversationChannel = 'ALL';

export enum ConversationChannelLabel {
  ALL = 'All',
  WEB = 'Web',
  MOBILE = 'Mobile app',
  PHONE_CALL = 'Phone call',
  WHATSAPP = 'WhatsApp',
  WEB_VIDEO = 'Web video',
  WEB_AUDIO = 'Web audio',
  MOBILE_VIDEO = 'Mobile video',
  MOBILE_AUDIO = 'Mobile audio',
  APP = 'App',
}

const CHANNEL_LABEL_MAP: Record<ConversationChannel, ConversationChannelLabel> =
  {
    ALL: ConversationChannelLabel.ALL,
    web: ConversationChannelLabel.WEB,
    mobile: ConversationChannelLabel.MOBILE,
    phone_call: ConversationChannelLabel.PHONE_CALL,
    whatsapp: ConversationChannelLabel.WHATSAPP,
    'web-video': ConversationChannelLabel.WEB_VIDEO,
    'web-audio': ConversationChannelLabel.WEB_AUDIO,
    'mobile-video': ConversationChannelLabel.MOBILE_VIDEO,
    'mobile-audio': ConversationChannelLabel.MOBILE_AUDIO,
    app: ConversationChannelLabel.APP,
  } as const;

const createChannelOptions = () => {
  return Object.entries(CHANNEL_LABEL_MAP).map(([value, label]) => ({
    value: value as ConversationChannel,
    label,
  }));
};

export const conversationChannelOptions = createChannelOptions();

export enum CompletedConversationsLabel {
  PAST = 'Past',
  ONGOING = 'Ongoing',
}

const createCompletedConversationsOptions = () => {
  return [
    { value: true, label: CompletedConversationsLabel.PAST },
    { value: false, label: CompletedConversationsLabel.ONGOING },
  ];
};

export const completedConversationsOptions =
  createCompletedConversationsOptions();

export enum AnalyticsMetricLabel {
  SUCCESS_RATE = 'Success Conversations',
  ERROR_RATE = 'Error Conversations',
  USER_ABANDONED = 'Customer Left Conversations',
  SYSTEM_AVAILABILITY = 'System Availability',
  TOPIC_TAGS = 'Conversation Volume by',
  MOOD_TAGS = 'Conversation Volume by',
}

const createAnalyticsMetricOptions = () => {
  return [
    {
      value: '',
      label: 'None',
    },
    {
      value: AnalyticsMetric.SUCCESS_RATE,
      label: AnalyticsMetricLabel.SUCCESS_RATE,
    },
    {
      value: AnalyticsMetric.ERROR_RATE,
      label: AnalyticsMetricLabel.ERROR_RATE,
    },
    {
      value: AnalyticsMetric.USER_ABANDONED,
      label: AnalyticsMetricLabel.USER_ABANDONED,
    },
    {
      value: AnalyticsMetric.TOPIC_TAGS,
      label: AnalyticsMetricLabel.TOPIC_TAGS,
    },
    {
      value: AnalyticsMetric.MOOD_TAGS,
      label: AnalyticsMetricLabel.MOOD_TAGS,
    },
  ];
};

export const analyticsMetricOptions = createAnalyticsMetricOptions();

export enum AnalyticsMetricValueLabel {
  SUCCESSFUL_CONVERSATIONS = 'Successful Conversations',
  NOT_SUCCESSFUL_CONVERSATIONS = 'Not Successful Conversations',
  CONVERSATIONS_WITH_ERROR = 'Conversations with Error',
  CONVERSATIONS_WITHOUT_ERROR = 'Conversations without Error',
  CONVERSATIONS_LEFT = 'Conversations Left',
  CONVERSATIONS_COMPLETED = 'Conversations Completed',
}

export enum AnalyticsMetricValue {
  SUCCESSFUL_CONVERSATIONS = 'successful_conversations',
  NOT_SUCCESSFUL_CONVERSATIONS = 'not_successful_conversations',
  CONVERSATIONS_WITH_ERROR = 'conversations_with_error',
  CONVERSATIONS_WITHOUT_ERROR = 'conversations_without_error',
  CONVERSATIONS_LEFT = 'conversations_left',
  CONVERSATIONS_COMPLETED = 'conversations_completed',
}

export const successRateValueOptions = [
  {
    value: AnalyticsMetricValue.SUCCESSFUL_CONVERSATIONS,
    label: AnalyticsMetricValueLabel.SUCCESSFUL_CONVERSATIONS,
  },
  {
    value: AnalyticsMetricValue.NOT_SUCCESSFUL_CONVERSATIONS,
    label: AnalyticsMetricValueLabel.NOT_SUCCESSFUL_CONVERSATIONS,
  },
];

export const errorRateValueOptions = [
  {
    value: AnalyticsMetricValue.CONVERSATIONS_WITH_ERROR,
    label: AnalyticsMetricValueLabel.CONVERSATIONS_WITH_ERROR,
  },
  {
    value: AnalyticsMetricValue.CONVERSATIONS_WITHOUT_ERROR,
    label: AnalyticsMetricValueLabel.CONVERSATIONS_WITHOUT_ERROR,
  },
];

export const userAbandonedValueOptions = [
  {
    value: AnalyticsMetricValue.CONVERSATIONS_LEFT,
    label: AnalyticsMetricValueLabel.CONVERSATIONS_LEFT,
  },
  {
    value: AnalyticsMetricValue.CONVERSATIONS_COMPLETED,
    label: AnalyticsMetricValueLabel.CONVERSATIONS_COMPLETED,
  },
];

export const getAnalyticsMetricValueOptions = (metricName: string) => {
  switch (metricName) {
    case AnalyticsMetric.SUCCESS_RATE:
      return successRateValueOptions;
    case AnalyticsMetric.ERROR_RATE:
      return errorRateValueOptions;
    case AnalyticsMetric.USER_ABANDONED:
      return userAbandonedValueOptions;
    default:
      return [];
  }
};

export const MetricsWithTags = [
  AnalyticsMetric.TOPIC_TAGS,
  AnalyticsMetric.MOOD_TAGS,
];
